import React, { Component } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import styled from "styled-components";
import {
    BusinessPortalHeader,
    PortalPageShell,
    PortalSelect,
    Toast
} from "src/components";
import StatisticsCard from "./components/StatisticsCard";
import Skeleton from "./components/Skeleton";
import { IsFeatureReady } from "src/constants/features";
import MoodDistributionCard from "./components/MoodDistributionCard";
import MoodRatingCard from "./components/MoodRatingCard";
import PrintableStatistics from "./components/StatisticsHiddenCanvas";
import StatisticsDownloadButton from "./components/StatisticsDownload";
import pluralize from "pluralize";
import { customNames, capitalizeString } from "src/utils/helpers";
import { UserContext } from "src/scenes/App/UserContext";
import { FEATURE_FLAGS } from "src/constants/FeatureFlag";

const DateRange = [
    "Last 7 Days",
    "Last 30 Days",
    "Last 3 Months",
    "Last 6 Months",
    "Last Year",
    "Lifetime",
];

const Label = styled.div`
  color: #171A22;
  font: normal normal 800 14px/19px Manrope;
  margin: 0 0 0.45rem;
  text-align: left;
  letter-spacing: 0;
  opacity: 1;
`;

const DropDown = styled.div`
  position: relative;
  display: inline-block;
  margin: 0;
  width: 14rem;

  @media (max-width: 560px) {
    width: 100%;
  }
`;

const ContentContainer = styled.div`
    width: 100%;
    max-width: 98.625rem;
    overflow: hidden;
    padding: 1rem;
    background: #ffffff 0 0 no-repeat padding-box;
    border: 1px solid #D8DEEC;
    border-radius: 16px;
    box-shadow: 0 18px 36px rgba(29, 42, 89, 0.08);
    opacity: 1;
    animation: statistics-dashboard-enter 0.24s ease both;
    box-sizing: border-box;
    display: grid;
    gap: 1.1rem;

    @keyframes statistics-dashboard-enter {
        from {
            opacity: 0;
            transform: translateY(8px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @media (max-width: 640px) {
        padding: 0.85rem;
    }
`;

const ContentCardRows = styled.div`
    display: grid;
    width: 100%;
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
    gap: 0.9rem;
    align-items: stretch;
    margin-bottom: 1.1rem;
    min-width: 0;

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const CardHeader = styled.div`
    margin: 0.5rem 0 0.85rem;
    text-align: left;
    font: normal normal 800 1rem/1.35 Manrope;
    letter-spacing: 0;
    color: #171A22;
    opacity: 1;
`;

const StatisticsHeaderControls = styled.div`
    display: grid;
    gap: 0.75rem;
    justify-items: start;
    min-width: 0;

    @media (max-width: 900px) {
        justify-items: stretch;
    }
`;

const StatisticsControlsSection = styled.section`
    width: 100%;
    max-width: 98.625rem;
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
    box-sizing: border-box;
    background: #ffffff;
    border: 1px solid #D8DEEC;
    border-radius: 16px;
    box-shadow: 0 18px 36px rgba(29, 42, 89, 0.08);
`;

const StatisticsDashboardGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.9fr);
    gap: 1.25rem;
    align-items: start;
    min-width: 0;

    @media (max-width: 1180px) {
        grid-template-columns: 1fr;
    }
`;

const StatisticsColumn = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.1rem;
    align-content: start;

    > * {
        min-width: 0;
    }
`;

const StatisticsFilterGrid = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;
    min-width: 0;

    @media (max-width: 900px) {
        justify-content: flex-start;
    }

    @media (max-width: 560px) {
        align-items: stretch;
        flex-direction: column;

        > div,
        select {
            width: 100%;
        }
    }
`;

class Statistics extends Component {
    static contextType = UserContext;
    state = {
        fetchingBaseNumbers: false,
        fetchingLineData: false,
        fetchingMoodTrackerData: false,
        dateRange: DateRange[0],
        type: "Employees",
        showPrintFormat: false,
        stats: {
            totalActiveEmployees: {},
            appSessions: {},
            retentionRate: {},
            totalPosts: 0,
            postViews: 0,
            interactionRate: {},
            quoteSaved: 0,
            articlesRead: 0,
            playlistsCount: 0,
            bodyAndMind: 0,
            recordPlaylistCounts: 0,
            popularCategory: {},
            teamGoals: 0,
            teamGoalsAchieved: {},
            achievedTeamGoals: {},
            userCounts: {
                Employees: 0,
                Clients: 0,
            },
            moodData: {
                overAllMoodRating: "Happy",
                moodCheckins: [
                    {
                        moodType: "Happy",
                        count: 35
                    },
                    {
                        moodType: "Content",
                        count: 25
                    },
                    {
                        moodType: "Neutral",
                        count: 20
                    },
                    {
                        moodType: "Sad",
                        count: 10
                    },
                    {
                        moodType: "Unhappy",
                        count: 7
                    },
                    {
                        moodType: "Tired",
                        count: 2
                    },
                    {
                        moodType: "Stressed",
                        count: 1
                    },
                    {
                        moodType: "Sick",
                        count: 1
                    }
                ]
            }
        },
    };

    setRange = (range) => {
        if (!this.state.fetchingBaseNumbers) {
            this.setState(
                {
                    dateRange: range,
                },
                () => {
                    this.fetchDateRange();
                }
            );
        }
    };

    setType = (type) => {
        if (!this.state.fetchingBaseNumbers) {
            this.setState(
                {
                    type: type,
                },
                () => {
                    this.fetchDateRange();
                }
            );
        }
    };

    componentDidMount() {
        this.fetchDateRange();
    }

    fetchLineData = () => {
        const { dateRange, type } = this.state;
        this.setState({ fetchingLineData: true });
        axios
            .get(
                `${API_URL}/company/statisticsLineData?filter=${dateRange}&type=${type}`,
                {
                    headers: {
                        Authorization: "Bearer " + this.props.user.token,
                    },
                }
            )
            .then((res) => {
                const {
                    totalActiveEmployees,
                    appSessions,
                    retentionRate,
                    postViews,
                    interactionRate,
                    teamGoalsAchieved
                } = res.data;
                this.setState({
                    stats: {
                        ...this.state.stats,
                        totalActiveEmployees: {
                            ...this.state.stats.totalActiveEmployees,
                            ...totalActiveEmployees,
                        },
                        appSessions: { ...this.state.stats.appSessions, ...appSessions },
                        retentionRate: {
                            ...this.state.stats.retentionRate,
                            ...retentionRate,
                        },
                        postViews: { ...this.state.stats.postViews, ...postViews },
                        interactionRate: {
                            ...this.state.stats.interactionRate,
                            ...interactionRate,
                        },
                        teamGoalsAchieved: {
                            ...this.state.stats.teamGoalsAchieved,
                            ...teamGoalsAchieved
                        }
                    },
                });
            })
            .catch((error) => {
                console.error(error);
                this.setState({
                    error: "An error occurred while fetching data. Please try again.",
                });
            })
            .finally(() => {
                this.setState({
                    fetchingLineData: false
                });
            });
    };

    fetchDateRange = () => {
        const { type, dateRange } = this.state;
        this.setState({
            fetchingBaseNumbers: true,
            fetchingMoodTrackerData: true
        });

        axios
            .get(`${API_URL}/company/statistics?filter=${dateRange}&type=${type}`, {
                headers: {
                    Authorization: "Bearer " + this.props.user.token,
                },
            }).then((res) => {
                if (res.status >= 200 && res.status <= 299) {
                    this.setState({
                        stats: { ...this.state.stats, ...res.data }
                    });
                    this.fetchMoodTrackerData();
                    this.fetchLineData();
                }
            }).catch((error) => {
                console.error(error);
                Toast.error("An error occurred while loading statistics.");
            }).finally(() => {
                this.setState({
                    fetchingBaseNumbers: false
                });
            });
    };

    fetchMoodTrackerData = () => {
        const { type, dateRange } = this.state;
        axios
            .get(`${API_URL}/company/moodTrackerData?filter=${dateRange}&type=${type}`, {
                headers: {
                    Authorization: "Bearer " + this.props.user.token,
                },
            })
            .then((res) => {
                if (res.status >= 200 && res.status <= 299) {
                    this.setState({
                        stats: { ...this.state.stats,
                            moodData: {
                                overAllMoodRating: res.data.overAllMoodRating,
                                moodCheckins: res.data.moodCheckins
                            }
                        },
                    });
                }
            }).catch((error) => {
                Toast.error(error.getApiErrorDetails().message);
                Toast.error("An error occurred while loading mood statistics.");
            }).finally(() => {
                this.setState({ fetchingMoodTrackerData: false });
            });
    };

    render() {
        const {
            fetchingBaseNumbers,
            fetchingLineData,
            fetchingMoodTrackerData,
            dateRange,
            stats: {
                totalActiveEmployees,
                appSessions,
                retentionRate,
                totalPosts,
                postViews,
                interactionRate,
                quoteSaved,
                articlesRead,
                bodyAndMind,
                recordPlaylistCounts,
                popularCategory,
                userCounts,
                moodData,
                teamGoals,
                achievedTeamGoals,
                teamGoalsAchieved
            },
            type,
        } = this.state;
        const users = type === "Employees" ? `${capitalizeString(pluralize(customNames.employees))}` : `${capitalizeString(pluralize(customNames.clients))}`;
        const pageHeadingLabel = type === "Employees" ? `${capitalizeString(pluralize.singular(customNames.employees))} Statistics` : `${capitalizeString(pluralize.singular(customNames.clients))} Statistics`;
        const dropdownEmployeeLabel = userCounts.Employees !== 0 ? `${capitalizeString(pluralize(customNames.employees))} (${userCounts.Employees})` : `${capitalizeString(pluralize(customNames.employees))}`;
        const dropdownClientLabel = userCounts.Clients !== 0 ? `${capitalizeString(pluralize(customNames.clients))} (${userCounts.Clients})` : `${capitalizeString(pluralize(customNames.clients))}`;
        const totalActiveEmployeesValue = totalActiveEmployees.value ? totalActiveEmployees.value : 0;
        const activeUserGraphData = totalActiveEmployees.lineGraphData ? totalActiveEmployees.lineGraphData : [];
        const retentionRateValue = retentionRate.value ? retentionRate.value : 0;
        const retentionRateGraphData = retentionRate.lineGraphData ? retentionRate.lineGraphData : [];
        const appSessionsValue = appSessions.value ? appSessions.value : 0;
        const appSessionsGraphData = appSessions.lineGraphData ? appSessions.lineGraphData : [];
        const totalPostsValue = totalPosts ? totalPosts : 0;
        const postViewsValue = postViews.value ? postViews.value : 0;
        const postViewsRateOfChange = postViews.rateOfChange ? postViews.rateOfChange : 0;
        const postViewsGraphData = postViews.lineGraphData ? postViews.lineGraphData : [];
        const interactionRateValue = interactionRate.value ? interactionRate.value : 0;
        const interactionRateGraphData = interactionRate.lineGraphData ? interactionRate.lineGraphData : [];
        const quoteSavedValue = quoteSaved.value ? quoteSaved.value : 0;
        const articlesReadValue = articlesRead.value ? articlesRead.value : 0;
        const recordPlaylistCountValue = recordPlaylistCounts.value ? recordPlaylistCounts.value : 0;
        const bodyAndMindValue = bodyAndMind.value ? bodyAndMind.value : 0;
        const totalTeamGoalsSet = teamGoals;
        const totalAchievedTeamGoal = achievedTeamGoals.value ? achievedTeamGoals.value : 0 ;
        const teamGoalsAchievedLineGraphData = teamGoalsAchieved.lineGraphData ? teamGoalsAchieved.lineGraphData : [];

        const userContext = this.context;
        const featureFlags = userContext?.featureFlags || [];
        const hasFeature = (f) => featureFlags.includes(f);

        const canViewStatistics = hasFeature(FEATURE_FLAGS.STATISTICS);
        const canViewNoticeboard = hasFeature(FEATURE_FLAGS.NOTICEBOARD);
        const canViewBodyAndMind = hasFeature(FEATURE_FLAGS.BODY_MIND);
        const canViewMyQuote = hasFeature(FEATURE_FLAGS.QUOTES);
        const canViewBusinessPlaylist = hasFeature([FEATURE_FLAGS.PLAYLISTS]);
        const canViewTeamGoals = hasFeature(FEATURE_FLAGS.TEAM_GOALS);
        const canViewModeTracker = hasFeature(FEATURE_FLAGS.MOOD_TRACKER);

        const hasContentEngagementData =
            canViewMyQuote ||
            canViewBodyAndMind ||
            Number(recordPlaylistCountValue) > 0;

        return (
            <PortalPageShell>
                <BusinessPortalHeader
                    title={pageHeadingLabel}
                    description={`Monitor active ${pluralize(customNames.employees.toLowerCase())} and ${pluralize(customNames.clients.toLowerCase())}, app engagement, content performance, and wellbeing trends to guide product and people decisions.`}
                />
                <StatisticsControlsSection>
                    <StatisticsHeaderControls>
                        <StatisticsFilterGrid>
                            <div>
                                <Label>Selected Statistics:</Label>
                                <DropDown>
                                    <PortalSelect value={type} onChange={(event) => this.setType(event.target.value)} width="14rem">
                                        <option value="Employees">
                                            {dropdownEmployeeLabel}
                                        </option>
                                        <option value="Clients">
                                            {dropdownClientLabel}
                                        </option>
                                    </PortalSelect>
                                </DropDown>
                            </div>
                            <div>
                                <Label>Filter:</Label>
                                <DropDown>
                                    <PortalSelect value={dateRange} onChange={(event) => this.setRange(event.target.value)} width="14rem">
                                        {DateRange.map((a, i) => {
                                            return (
                                                <option
                                                    key={i}
                                                    value={a}
                                                >
                                                    {a}
                                                </option>
                                            );
                                        })}
                                    </PortalSelect>
                                </DropDown>
                            </div>
                            <StatisticsDownloadButton
                                fileName={pageHeadingLabel}
                                dateRange={dateRange}
                                type={type}
                                user={this.props.user}
                                isDataLoading={fetchingLineData || fetchingMoodTrackerData || fetchingBaseNumbers }
                            />
                        </StatisticsFilterGrid>
                    </StatisticsHeaderControls>
                </StatisticsControlsSection>
                <div>
                    <ContentContainer style={{ zIndex: 1, position: "relative" }}>
                        <StatisticsDashboardGrid>
                            <StatisticsColumn>
                                <CardHeader>
                                    {fetchingBaseNumbers ?
                                        <Skeleton
                                            variant="Text"
                                            width="150px"
                                            height="25px"
                                            animation="wave"
                                        />
                                        :
                                        "User Engagement"
                                    }
                                </CardHeader>
                                {canViewStatistics && (
                                    <>
                                        <ContentCardRows>
                                            <StatisticsCard
                                                header={`Total Active ${users}`}
                                                footer={`Total number of Active ${users} within a specific period.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${totalActiveEmployeesValue}`}
                                                rate={Math.round(totalActiveEmployees.rateOfChange)}
                                                loadingLineGraph={fetchingLineData}
                                                graphData={activeUserGraphData}
                                            ></StatisticsCard>
                                            <StatisticsCard
                                                header="Retention Rate"
                                                footer={`Percentage of ${users} who continue using the app over time.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${`${parseFloat(retentionRateValue).toFixed()}%`}`}
                                                rate={Math.round(retentionRate.rateOfChange)}
                                                loadingLineGraph={fetchingLineData}
                                                graphData={retentionRateGraphData}
                                            ></StatisticsCard>
                                            <StatisticsCard
                                                header="App Sessions"
                                                footer={`Average Sessions per ${users}.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${appSessionsValue}`}
                                                rate={Math.round(appSessions.rateOfChange)}
                                                loadingLineGraph={fetchingLineData}
                                                graphData={appSessionsGraphData}
                                            ></StatisticsCard>
                                        </ContentCardRows>
                                    </>
                                )}
                                {canViewNoticeboard && (
                                    <>
                                        <CardHeader>
                                            {fetchingBaseNumbers ?
                                                <Skeleton
                                                    variant="Text"
                                                    width="150px"
                                                    height="25px"
                                                    animation="wave"
                                                />
                                                :
                                                "Business Noticeboard"
                                            }
                                        </CardHeader>
                                        <ContentCardRows>
                                            <StatisticsCard
                                                header="Total Posts"
                                                footer={`Number of posts by admins on the ${users} Noticeboard.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${totalPostsValue}`}
                                            ></StatisticsCard>
                                            <StatisticsCard
                                                header="Post Impressions"
                                                footer={`Total new posts viewed by all ${users}.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${postViewsValue}`}
                                                rate={postViewsRateOfChange}
                                                loadingLineGraph={fetchingLineData}
                                                graphData={postViewsGraphData}
                                            ></StatisticsCard>
                                            {IsFeatureReady.interactionRateStat() && (
                                                <StatisticsCard
                                                    header="Interaction Rate"
                                                    footer={`How many ${users} engaged with the posted content.`}
                                                    loadingBaseNumbers={fetchingBaseNumbers}
                                                    total={`${interactionRateValue}`}
                                                    rate={Math.round(interactionRate.rateOfChange)}
                                                    loadingLineGraph={fetchingLineData}
                                                    graphData={interactionRateGraphData}
                                                ></StatisticsCard>
                                            )}
                                        </ContentCardRows>
                                    </>
                                )}

                                {hasContentEngagementData && (
                                    <>
                                        <CardHeader>
                                            {fetchingBaseNumbers ?
                                                <Skeleton
                                                    variant="Text"
                                                    width="150px"
                                                    height="25px"
                                                    animation="wave"
                                                />
                                                :
                                                "Content Engagement"
                                            }
                                        </CardHeader>
                                    </>
                                )}
                                <ContentCardRows>
                                    {canViewMyQuote && (
                                        <>
                                            <StatisticsCard
                                                header="Quotes Saved"
                                                footer={`Number of times ${users} have saved quotes to favourites.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${quoteSavedValue}`}
                                            ></StatisticsCard>
                                            <StatisticsCard
                                                header="Most Popular Quote Category"
                                                footer={`The most popular quote category selected by ${users}.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${popularCategory}`}
                                            ></StatisticsCard>
                                            <StatisticsCard
                                                header="Articles Read"
                                                footer={`Number of times ${users} have opened and viewed an article.`}
                                                loadingBaseNumbers={fetchingBaseNumbers}
                                                total={`${articlesReadValue}`}
                                            ></StatisticsCard>
                                        </>
                                    )}
                                    {canViewBusinessPlaylist && (
                                        <StatisticsCard
                                            header="Business Playlist"
                                            footer={`Number of times ${users} have selected to open your business playlists.`}
                                            loadingBaseNumbers={fetchingBaseNumbers}
                                            total={`${recordPlaylistCountValue}`}
                                        ></StatisticsCard>
                                    )}
                                    {canViewBodyAndMind && (
                                        <StatisticsCard
                                            header="Body & Mind Content Saved"
                                            footer={`Number of times ${users} have saved content in the Body & Mind feature.`}
                                            loadingBaseNumbers={fetchingBaseNumbers}
                                            total={`${bodyAndMindValue}`}
                                        ></StatisticsCard>
                                    )}
                                </ContentCardRows>

                            </StatisticsColumn>

                            <StatisticsColumn>
                                { users === "Employees" ?
                                    <>
                                        {canViewTeamGoals && (
                                            <>
                                                <CardHeader>
                                                    {fetchingBaseNumbers ?
                                                        <Skeleton
                                                            variant="Text"
                                                            width="150px"
                                                            height="25px"
                                                            animation="wave"
                                                        />
                                                        :
                                                        "Team Goal Tracking"
                                                    }
                                                </CardHeader>
                                                <ContentCardRows>
                                                    <StatisticsCard
                                                        header="Team Goals Set"
                                                        footer="Number of team goals set by employees."
                                                        loadingBaseNumbers={fetchingBaseNumbers}
                                                        total={`${totalTeamGoalsSet}`}
                                                        graphData={0}
                                                    ></StatisticsCard>
                                                    <StatisticsCard
                                                        header="Achieved Team Goals"
                                                        footer="Percentage of achieved team goals."
                                                        loadingBaseNumbers={fetchingBaseNumbers}
                                                        total={`${totalAchievedTeamGoal}`}
                                                        rate={Math.round(retentionRate.rateOfChange)}
                                                        loadingLineGraph={fetchingLineData}
                                                        graphData={teamGoalsAchievedLineGraphData}
                                                    ></StatisticsCard>
                                                </ContentCardRows>
                                            </>
                                        )}
                                    </>
                                    : <> </>}

                                {canViewModeTracker && (
                                    <>
                                        <CardHeader>
                                            {fetchingBaseNumbers ?
                                                <Skeleton
                                                    variant="Text"
                                                    width="150px"
                                                    height="25px"
                                                    animation="wave"
                                                />
                                                :
                                                `${users} Mood Tracker`
                                            }
                                        </CardHeader>
                                        <ContentCardRows>
                                            <MoodRatingCard
                                                header="Overall Mood Rating"
                                                footer={`Overall mood rating for ${users}.`}
                                                loadingBaseNumbers={fetchingMoodTrackerData}
                                                moodData={moodData}
                                            >
                                            </MoodRatingCard>
                                            <MoodDistributionCard
                                                header="Mood Distribution"
                                                footer={`Mood distribution across all ${users}.`}
                                                loadingBaseNumbers={fetchingMoodTrackerData}
                                                cardType="Mood"
                                                moodData={moodData}
                                            />
                                        </ContentCardRows>
                                    </>
                                )}
                            </StatisticsColumn>
                        </StatisticsDashboardGrid>
                    </ContentContainer>

                    <PrintableStatistics
                        companyName={this.props.user.companyName}
                        dateRange={dateRange}
                        users={users}
                        fetchingBaseNumbers={fetchingBaseNumbers}
                        totalActiveEmployees={totalActiveEmployees}
                        totalActiveEmployeesValue={totalActiveEmployeesValue}
                        fetchingLineData={fetchingLineData}
                        activeUserGraphData={activeUserGraphData}
                        retentionRate={retentionRate}
                        retentionRateValue={retentionRateValue}
                        retentionRateGraphData={retentionRateGraphData}
                        appSessions={appSessions}
                        appSessionsGraphData={appSessionsGraphData}
                        appSessionsValue={appSessionsValue}
                        totalPostsValue={totalPostsValue}
                        postViewsValue={postViewsValue}
                        postViewsRateOfChange={postViewsRateOfChange}
                        postViewsGraphData={postViewsGraphData}
                        interactionRateValue={interactionRateValue}
                        interactionRate={interactionRate}
                        interactionRateGraphData={interactionRateGraphData}
                        quoteSavedValue={quoteSavedValue}
                        popularCategory={popularCategory}
                        articlesReadValue={articlesReadValue}
                        recordPlaylistCountValue={recordPlaylistCountValue}
                        bodyAndMindValue={bodyAndMindValue}
                        totalTeamGoalsSet={totalTeamGoalsSet}
                        totalAchievedTeamGoal={totalAchievedTeamGoal}
                        teamGoalsAchievedLineGraphData={teamGoalsAchievedLineGraphData}
                        fetchingMoodTrackerData={fetchingMoodTrackerData}
                        moodData={moodData}
                    />
                </div>
            </PortalPageShell>
        );
    }
}

export default Statistics;
